import { useApp, useButton } from '@libs/hooks';
import { AssetsSvgMap, ECalculatorErrorType, TBaseCalculatorData, TCalculatorError, TWidget } from '@libs/types';
import { SplitText } from '@libs/utils';
import React from 'react';
import { ButtonCircle } from '../../Buttons/ButtonCircle';
import { ButtonRow } from '../../Buttons/ButtonRow';
import { PopupComponent } from '../../UI/Popup/PopupComponent';
import { TextCommon } from '../../UI/Text/TextCommon';
import styles from '../Calculator.module.css';
import { sanitiseServerValue } from '../utils/format/format';
import { ActionNameEnum, InputEnum, THandleButtonClick, THandleUpdateValue } from '../utils/types';

type TCalculatorErrorProps = {
    widget: TWidget<TBaseCalculatorData>;
    handleUpdateValue: THandleUpdateValue;
    handleButtonClick?: THandleButtonClick;
    name: InputEnum;
    newInputValue?: string;
    error: TCalculatorError;
    buttonGotItLabel?: string;
    buttonIncreaseLimitLabel?: string;
    buttonChangeAmountLabel?: string;
    buttonChangeDeliveryOptionLabel?: string;
    buttonChangeTransferOptionLabel?: string;
};

const CalculatorError = ({
    widget,
    newInputValue,
    name,
    handleUpdateValue,
    error,
    handleButtonClick,
    buttonGotItLabel = '',
    buttonIncreaseLimitLabel = '',
    buttonChangeAmountLabel = '',
    buttonChangeDeliveryOptionLabel = '',
    buttonChangeTransferOptionLabel = '',
}: TCalculatorErrorProps) => {
    const { setPopup } = useApp();
    const { handleClickByActionName } = useButton(widget);
    const isShowButtonCircle = error?.type !== ECalculatorErrorType.MIN_AMOUNT;
    const onChangeAmountClick = () => {
        if (newInputValue) {
            const isFrom = name === InputEnum.FROM_INPUT;
            handleUpdateValue(sanitiseServerValue(newInputValue), name, isFrom);
        }
        setPopup(null);
    };

    const handleClick = (actionName?: ActionNameEnum) => {
        setPopup(null);
        if (actionName) {
            handleClickByActionName(actionName);
        }
    };

    const getErrorButtons = () => {
        const buttons: Array<React.ReactNode> = [];

        if (error?.increaseLimitOptions) {
            buttons.push(
                <ButtonRow
                    key={ActionNameEnum.increaseLimit}
                    isWidget={false}
                    isHide={false}
                    disabled={false}
                    label={buttonIncreaseLimitLabel}
                    iconName="ic_arrow_up"
                    handleClick={() => handleClick(ActionNameEnum.increaseLimit)}
                />,
            );
        }
        if (error?.changeAmount) {
            buttons.push(
                <ButtonRow
                    key="changeAmount"
                    isWidget={false}
                    isHide={false}
                    disabled={false}
                    label={buttonChangeAmountLabel}
                    iconName="ic_arrow_retry"
                    handleClick={onChangeAmountClick}
                />,
            );
        }
        if (error?.changeDeliveryOptions) {
            buttons.push(
                <ButtonRow
                    key={ActionNameEnum.onChangeDeliveryOptions}
                    isWidget={false}
                    isHide={false}
                    disabled={false}
                    label={buttonChangeDeliveryOptionLabel}
                    iconName="ic_arrow_back"
                    handleClick={() => handleClick(ActionNameEnum.onChangeDeliveryOptions)}
                />,
            );
        }
        if (error?.withdrawButtonVisible) {
            buttons.push(
                <ButtonRow
                    key="withdrawButtonVisible"
                    isWidget={false}
                    isHide={false}
                    disabled={false}
                    label={`${error?.withdrawButtonText}${error?.maxWithdrawValue} ${error?.withdrawCurIso?.toUpperCase() ?? ''}`}
                    iconName="ic_arrow_forward"
                    handleClick={() => {
                        setPopup(null);
                        handleButtonClick?.();
                    }}
                />,
            );
        }
        if (error?.changeTransferOptions) {
            buttons.push(
                <ButtonRow
                    key={ActionNameEnum.onChangeTransferOptions}
                    isWidget={false}
                    isHide={false}
                    disabled={false}
                    label={buttonChangeTransferOptionLabel}
                    iconName="ic_arrow_back"
                    handleClick={() => handleClick(ActionNameEnum.onChangeTransferOptions)}
                />,
            );
        }
        if (error?.gotIt) {
            buttons.push(
                <ButtonRow isWidget={false} isHide={false} disabled={false} label={buttonGotItLabel} iconName="ic_check" handleClick={() => handleClick()} />,
            );
        }
        return buttons?.map((button, index) => <div key={index}>{button}</div>);
    };

    const handleErrorClick = () => {
        setPopup(<PopupComponent header={<SplitText text={error?.longText ?? ''} />} externalContent={[getErrorButtons()]} />);
    };

    return (
        <span className={styles.errorWrapper}>
            <TextCommon isWidget={false} as="span" content={error?.shortText ?? ''} options={{ variant: 'secondary', textColor: 'pink_100' }} />
            {isShowButtonCircle && (
                <ButtonCircle
                    isWidget={false}
                    size="small"
                    iconName={AssetsSvgMap.ic_info_circle_small}
                    hierarchy="destructive"
                    iconWidth={16}
                    iconHeight={16}
                    handleClick={handleErrorClick}
                />
            )}
        </span>
    );
};
export default React.memo(CalculatorError);
